import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { getPdfUrl } from '@/features/story/api';
import { isPwaStandalone } from '@/lib/platform';
import { STRINGS } from '@/strings/strings';
import type { PdfStatus } from '@/features/story/types';

interface Props {
  storyId: string;
  status: PdfStatus;
  storyTitle?: string;
}

/**
 * Story-Page PDF action button with pre-fetched signed URL.
 *
 * Wired in Batch 12 Sub-phase A — replaces the 6.2.1 scaffold (the
 * stub `onClick={() => {}}` was the root cause of Sebastian's
 * 2026-04-27 DevTools observation: "click is a pure no-op").
 *
 * State matrix:
 *   - `not_requested`        → renders nothing
 *   - `pending`/`generating` → disabled, "📄 PDF wird erstellt…"
 *   - `failed`               → static failure label "📄 PDF nicht verfügbar"
 *   - `ready` (idle)         → enabled "📄 PDF herunterladen"
 *   - `ready` + isLoading    → disabled "📄 PDF wird geladen…"
 *   - `ready` + error        → enabled button + inline error + retry-link
 *
 * Click flow (PWA #16 Sub-Phase 16.6 final form — pre-fetch + split
 * branches per launch context):
 *   1. On mount when `status === 'ready'`, fire `getPdfUrl(storyId)`
 *      in a useEffect and stash the signed URL in component state.
 *      Pre-fetching means the button click is synchronous — the
 *      iOS Safari user-gesture token (transient activation) is still
 *      live when the anchor or `navigate()` runs, so iOS opens the
 *      new tab cleanly instead of silently dropping the click.
 *      Trade-off: one extra `getPdfUrl()` call per Story-Output view
 *      that has a ready PDF, even if the user never taps. Negligible
 *      at beta volume; URL has 1h TTL → existing error-UI handles
 *      the rare expired-URL case if a session lasts > 1h.
 *   2. Click handler branches on `isPwaStandalone()`:
 *      - **Browser** (Mobile Safari + Desktop): build a transient
 *        `<a target="_blank" rel="noopener,noreferrer">`, append,
 *        programmatically `.click()`, remove. No `download` attribute
 *        — Sebastian's UX intent is view-then-decide-to-save, and
 *        native QuickLook already provides save/share controls in
 *        the new tab.
 *      - **Homescreen-PWA**: navigate to `/story/:storyId/pdf` with
 *        the signed URL + storyTitle passed via router state. The
 *        in-app PdfViewerRoute renders the PDF via react-pdf with
 *        pinch-zoom and a download-button overlay (PWA cannot rely
 *        on `target="_blank"` — anchor-click silently does nothing
 *        on iOS Homescreen-PWA).
 *   3. Race fallback: if the user clicks before the pre-fetch has
 *      resolved, fall back to the original async fetch + click.
 *      Same retry semantics as before — 409 `story_pdf_not_ready`
 *      waits 1s then retries once. iOS Safari Browser may drop this
 *      async-path click silently (gesture token expired across the
 *      await), but the race window is narrow (pre-fetch fires on
 *      mount, completes in ~200ms) and the existing error-UI surfaces
 *      a "Erneut versuchen" link if it does.
 *
 * Hybrid copy: `ApiError.envelope.user_message` preferred over the
 * PWA fallback (Sebastian "use existing texts" mandate — backend >
 * PWA fallback precedence per Sub-phase A spec).
 */
export function PdfPlaceholderButton({ storyId, status, storyTitle }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefetchedUrl, setPrefetchedUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const copy = STRINGS.storyOutput.page.pdf;

  // Pre-fetch the signed PDF URL on mount when the PDF is ready, so
  // the click handler can deliver synchronously and survive the iOS
  // Safari transient-activation rule. See JSDoc above for rationale.
  useEffect(() => {
    if (status !== 'ready') return;
    let cancelled = false;
    getPdfUrl(storyId)
      .then(({ url }) => {
        if (!cancelled) setPrefetchedUrl(url);
      })
      .catch(() => {
        // Swallow — the click handler will retry on demand. Surfacing
        // a pre-fetch error before the user has expressed intent would
        // be noisy and unhelpful (the button is still usable via the
        // async fallback path).
      });
    return () => {
      cancelled = true;
    };
  }, [status, storyId]);

  if (status === 'not_requested') return null;

  if (status === 'failed') {
    return (
      <div
        aria-disabled
        className="h-11 w-full rounded-lg border border-error/40 bg-dream-background/40 text-center text-sm text-error line-through leading-[44px]"
      >
        {copy.failed}
      </div>
    );
  }

  if (status === 'pending' || status === 'generating') {
    return (
      <div
        aria-disabled
        aria-live="polite"
        className="h-11 w-full animate-pulse motion-reduce:animate-none rounded-lg bg-dream-gold text-center text-sm text-dream-navy leading-[44px]"
      >
        {copy.generating}
      </div>
    );
  }

  // status === 'ready' (with possible isLoading + error overlays)

  function resolveErrorMessage(err: unknown): string {
    if (err instanceof ApiError && err.envelope?.user_message) {
      return err.envelope.user_message;
    }
    return copy.errors.loadFailed;
  }

  function openInNewTab(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener,noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function deliver(url: string) {
    if (isPwaStandalone()) {
      navigate(`/story/${storyId}/pdf`, { state: { url, storyTitle } });
    } else {
      openInNewTab(url);
    }
  }

  function handleClick() {
    setError(null);
    if (prefetchedUrl) {
      // Sync path — gesture token alive, iOS opens new tab cleanly.
      deliver(prefetchedUrl);
      return;
    }
    void handleAsyncClick();
  }

  async function handleAsyncClick() {
    setIsLoading(true);
    try {
      const { url } = await getPdfUrl(storyId);
      setPrefetchedUrl(url);
      deliver(url);
    } catch (err) {
      // 409 race: pdfStatus='ready' in DTO but signing in flight.
      // Retry once after 1s before surfacing the error.
      if (err instanceof ApiError && err.errorCode === 'story_pdf_not_ready') {
        await new Promise((r) => setTimeout(r, 1000));
        try {
          const { url } = await getPdfUrl(storyId);
          setPrefetchedUrl(url);
          deliver(url);
        } catch (retryErr) {
          setError(resolveErrorMessage(retryErr));
        }
      } else {
        setError(resolveErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        className="h-11 w-full rounded-lg bg-dream-gold text-sm font-bold text-dream-navy transition hover:bg-dream-gold/90 disabled:cursor-not-allowed disabled:opacity-60"
        data-testid="pdf-button"
      >
        {isLoading ? copy.loading : copy.ready}
      </button>
      {error && (
        <p role="alert" className="text-xs text-error" data-testid="pdf-error">
          {error}{' '}
          <button
            type="button"
            onClick={handleClick}
            className="font-medium underline"
            data-testid="pdf-retry"
          >
            {copy.errors.retry}
          </button>
        </p>
      )}
    </div>
  );
}
